<script setup>
import _ from 'lodash'
import { ref, onMounted, computed, watch, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import moment from 'moment'
import IconBase from '../icon/IconBase.vue'
import IconGlobe from '../icon/IconGlobe.vue'
import IconCalendar from '../icon/IconCalendar.vue'
import IconClock from '../icon/IconClock.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import FormButton from '../Atoms/FormButton.vue'
import StreamService from '@/services/StreamService'
import FormInput from '../Atoms/FormInput.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue';
import { track, EVENTS } from '@/lib/tracking';

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  playerUrl: {
    type: String
  },
  videoMeta: {
    type: Object
  }
})

const emit = defineEmits({
  'video-metadata-schedule-updated': () => true,
})

const schedule = ref({
  publishDate: '',
  publishTime: '',
  expireDate: '',
  expireTime: '',
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  enableExpiry: false,
})

const scheduleValidation = ref({
  publishDate: '',
  publishTime: '',
  expiryDate: '',
  expiryTime: '',
})

const timeZoneDropdown = ref(null)

const timeZoneFilter = ref('')
const bypassScheduleUpdate = ref(false)
const saving = ref(false)

const savedSnapshot = ref(null)
const scheduleSnapshot = () =>
  _.cloneDeep(_.pick(schedule.value, ['publishDate', 'publishTime', 'expireDate', 'expireTime', 'enableExpiry']))
const isDirty = computed(() => !_.isEqual(scheduleSnapshot(), savedSnapshot.value))

const saveSchedule = async () => {
  if (bypassScheduleUpdate.value) return
  if (!validateScheduleDateTime()) return
  saving.value = true
  try {
    await updateScheduleDetails(false)
    savedSnapshot.value = scheduleSnapshot()
    track(EVENTS.VIDEO_SCHEDULE_SAVED, {
      folderId: props.vodFolder?._id,
      fileId: props.vodFile?._id,
      enableExpiry: schedule.value.enableExpiry,
    })
    notify({ group: 'success', text: 'Schedule saved successfully' })
  } catch (e) {
    notify({ group: 'error', text: e?.message || 'Could not save schedule' })
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  getSchedule()
})

watch(() => `${route.params.streamId}/${route.params.videoId}`, async () => {
  resetScheduleErrors()
  getSchedule()
})

const getSchedule = async () => {
  const scheduleMeta = props?.videoMeta?.schedule ?? props?.vodFile?.schedule;
  if (scheduleMeta) {
    bypassScheduleUpdate.value = true
    schedule.value = _.assign({}, schedule.value, scheduleMeta);
    if (schedule.value.publishTime) {
      let timePart = schedule.value.publishTime.split('T')?.[1];
      timePart = timePart.substring(0, timePart.length - 4);
      const timeArray = timePart.split(':')
      schedule.value.publishTime = {
        hours: parseInt(timeArray[0], 10),
        minutes: parseInt(timeArray[1], 10),
        seconds: parseInt(timeArray[2], 10)
      }
    }
    if (schedule.value.expireTime) {
      let timePart = schedule.value.expireTime.split('T')?.[1];
      timePart = timePart.substring(0, timePart.length - 4);
      const timeArray = timePart.split(':')
      schedule.value.expireTime = {
        hours: parseInt(timeArray[0], 10),
        minutes: parseInt(timeArray[1], 10),
        seconds: parseInt(timeArray[2], 10)
      }
    }
    bypassScheduleUpdate.value = false
  }
  savedSnapshot.value = scheduleSnapshot()
}

const toggleExpiryEnable = async () => {
  const curr = schedule.value.enableExpiry;
  updateScheduleDetails(curr);
}

const timeZoneFormatted = (timeZone) => {
  if (timeZone) {
    return `(GMT${moment().tz(timeZone).utcOffset() / 60 >= 0 ? '+ ' + moment().tz(timeZone).utcOffset() / 60 : moment().tz(timeZone).utcOffset() / 60}) ${timeZone}`
  }
}
const timeZonesFiltered = computed(() => {
  let array = Intl.supportedValuesOf('timeZone').filter(element => element.toLowerCase().includes(timeZoneFilter.value.toLowerCase()))
  const sortedArray = array.sort(function (a, b) {
    return moment().tz(a).utcOffset() / 60 - moment().tz(b).utcOffset() / 60
  })
  return sortedArray
})

const updateScheduleDetails = async (enableValidation = true) => {
  if (bypassScheduleUpdate.value) {
    return
  }
  if (enableValidation && !validateScheduleDateTime()) return;

  const scheduleCopy = _.cloneDeep(schedule.value)
  if (schedule.value.publishTime && schedule.value.publishDate) {
    const publishDate = schedule.value.publishDate
    const publishTime = schedule.value.publishTime
    const datePart = publishDate.split('T')?.[0]

    scheduleCopy.publishTime = `${datePart}T${publishTime.hours < 10 ? ('0' + publishTime.hours) : publishTime.hours}:${publishTime.minutes < 10 ? ('0' + publishTime.minutes) : publishTime.minutes}:${publishTime.seconds < 10 ? ('0' + publishTime.seconds) : publishTime.seconds}.000Z`
  }

  if (schedule.value.expireTime && schedule.value.expireDate) {
    const expireDate = schedule.value.expireDate
    const expireTime = schedule.value.expireTime
    const datePart = expireDate.split('T')?.[0]

    scheduleCopy.expireTime = `${datePart}T${expireTime.hours < 10 ? ('0' + expireTime.hours) : expireTime.hours}:${expireTime.minutes < 10 ? ('0' + expireTime.minutes) : expireTime.minutes}:${expireTime.seconds < 10 ? ('0' + expireTime.seconds) : expireTime.seconds}.000Z`
  }

  if (route.params.videoId.includes('.mp4')) {
    await StreamService.updateStreamPlaylistVideoMetaData(route.params.streamId, route.params.videoId,
      {
        schedule: scheduleCopy
      }
    )
    StreamService.clearStreamMetadataCache(route.params.streamId, route.params.videoId.replace('.mp4', ''));
  } else {
    await StreamService.saveStreamMetadata(route.params.streamId, 'schedule', scheduleCopy, props.vodFile._id);
    const url = props.playerUrl;
    let key = undefined;
    if (url.includes('?')) {
      key = url.split('?')[1]
    }
    StreamService.clearStreamMetadataCache(route.params.streamId, key);
  }

  emit('video-metadata-schedule-updated', scheduleCopy);
}

// publishTime/expireTime are { hours, minutes, seconds } objects (see getSchedule),
// so build the absolute UTC timestamp from the date + time parts the same way
// updateScheduleDetails does. Returns NaN when the date or time is not yet set.
const toTimestamp = (dateVal, timeVal) => {
  if (!dateVal || !timeVal || typeof timeVal !== 'object') return NaN;
  const datePart = String(typeof dateVal === 'string' ? dateVal : new Date(dateVal).toISOString()).split('T')[0];
  const pad = (n) => String(n ?? 0).padStart(2, '0');
  return new Date(`${datePart}T${pad(timeVal.hours)}:${pad(timeVal.minutes)}:${pad(timeVal.seconds)}.000Z`).getTime();
};

const validateScheduleDateTime = () => {
  resetScheduleErrors();

  const { enableExpiry, expireDate, expireTime, publishDate, publishTime } = schedule.value;

  const absPubTimeInMs = toTimestamp(publishDate, publishTime);
  if (!Number.isNaN(absPubTimeInMs) && Date.now() > absPubTimeInMs) {
    scheduleValidation.value.publishTime = 'The entered time is already passed.';
    return false;
  }

  if (enableExpiry) {
    const absExpiryTimeInMs = toTimestamp(expireDate, expireTime);

    if (Number.isNaN(absExpiryTimeInMs) || Number.isNaN(absPubTimeInMs)) {
      scheduleValidation.value.expiryTime = 'Please set both a publish and expiration date & time.';
      return false;
    }

    if (absExpiryTimeInMs <= absPubTimeInMs) {
      scheduleValidation.value.expiryTime = 'Expiration date & time should be later than the publish date.';
      return false;
    }
  }

  return true;
}

const changeTimeZone = (timeZone) => {
  schedule.value.timeZone = timeZone
  updateScheduleDetails()
  timeZoneDropdown.value.toggleDropdown()
}

const resetScheduleErrors = () => {
  scheduleValidation.value.publishTime = '';
  scheduleValidation.value.expiryTime = '';
}
</script>

<template>
  <section class="flex flex-col gap-4">
    <!-- Header banner -->
    <div class="rounded-md bg-surface-3 p-3 flex gap-2.5">
      <icon-base class="shrink-0 text-surface-8 mt-0.5" fill="none">
        <icon-calendar />
      </icon-base>
      <div class="min-w-0">
        <h4 class="text-sm font-medium text-white">Schedule VOD Availability</h4>
        <p class="text-xs text-surface-8 mt-0.5">Set when this video file starts and stops being available to viewers.
          The video will only be played during the scheduled period.</p>
      </div>
    </div>

    <!-- Publish date & time -->
    <div>
      <label class="text-sm text-surface-9 block !mb-1.5">Publish date &amp; time</label>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div class="flex items-center gap-2 relative">
          <IconBase class="absolute left-2 z-10 text-surface-8" fill="none">
            <icon-calendar />
          </IconBase>
          <VueDatePicker input-id="startDate" utc :enable-time-picker="false" v-model="schedule.publishDate"
            inputClassName="h-9 bg-surface-3 hover:bg-surface-4 rounded border-none"
            @update:model-value="updateScheduleDetails"
            :class="scheduleValidation.publishDate ? 'border border-error' : 'border-none'" />
        </div>
        <div class="flex items-center gap-2 relative">
          <IconBase class="absolute left-2 z-10 text-surface-8" fill="none">
            <icon-clock />
          </IconBase>
          <VueDatePicker input-id="start-time-selector" utc enable-seconds time-picker format="HH:mm:ss"
            v-model="schedule.publishTime" inputClassName="h-9 bg-surface-3 hover:bg-surface-4 rounded border-none"
            @update:model-value="updateScheduleDetails"
            :class="scheduleValidation.publishTime ? 'border border-error' : 'border-none'" />
        </div>
      </div>
      <p v-if="scheduleValidation.publishTime" class="text-error text-tiny mt-1">{{ scheduleValidation.publishTime }}
      </p>
    </div>

    <!-- Time zone -->
    <div>
      <label class="text-sm text-surface-9 block !mb-1.5">Time zone</label>
      <DropDownMenu ref="timeZoneDropdown"
        customClasses="flex items-center justify-between truncate w-full bg-surface-3 rounded-md h-9 !px-3 hover:bg-surface-4 text-tiny"
        wrapperClass="w-full" :maxHeight="64" autofocus-menu-input>
        <template #toggle-button>
          <span class="flex items-center min-w-0 truncate">
            <icon-base class="!mr-2 shrink-0 text-surface-8" fill="none">
              <icon-globe />
            </icon-base>
            <span class="truncate">{{ timeZoneFormatted(schedule.timeZone) }}</span>
          </span>
          <icon-base class="!ml-1 shrink-0">
            <icon-chevron-up-down />
          </icon-base>
        </template>
        <template #menu-items>
          <DropDownMenuItem isCustom class="!p-2 hover:!bg-transarent">
            <FormInput v-model="timeZoneFilter" />
          </DropDownMenuItem>
          <DropDownMenuItem v-for="(timeZone, index) in timeZonesFiltered" :key="index"
            @click="changeTimeZone(timeZone)">
            {{ timeZoneFormatted(timeZone) }}
          </DropDownMenuItem>
        </template>
      </DropDownMenu>
    </div>

    <!-- Set expiration date -->
    <div class="flex items-start justify-between gap-3 border-t border-surface-3 pt-4">
      <div class="min-w-0">
        <p class="text-sm text-surface-text-1">Set expiration date</p>
        <p class="text-xs text-surface-8 mt-0.5">Video will stop being available after this date</p>
      </div>
      <FormSwitch class="shrink-0 mt-0.5" size="sm" v-model="schedule.enableExpiry" @change="toggleExpiryEnable" />
    </div>

    <div v-if="schedule.enableExpiry">
      <label class="text-sm text-surface-9 block !mb-1.5">Expiration date &amp; time</label>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div class="flex items-center gap-2 relative">
          <IconBase class="absolute left-2 z-10 text-surface-8" fill="none">
            <icon-calendar />
          </IconBase>
          <VueDatePicker input-id="endDate" :min-date="schedule.publishDate"
            :disabled="!schedule.publishDate || !schedule.publishTime" utc :enable-time-picker="false"
            v-model="schedule.expireDate" @update:model-value="updateScheduleDetails"
            inputClassName="h-9 bg-surface-3 hover:bg-surface-4 disabled:text-surface-6 disabled:bg-surface-2 hover:disabled:bg-surface-2 rounded border-none"
            :class="scheduleValidation.expiryTime ? 'border border-error' : 'border-none'" />
        </div>
        <div class="flex items-center gap-2 relative">
          <IconBase class="absolute left-2 z-10 text-surface-8" fill="none">
            <icon-clock />
          </IconBase>
          <VueDatePicker :disabled="!schedule.publishDate || !schedule.publishTime" utc enable-seconds time-picker
            format="HH:mm:ss" v-model="schedule.expireTime"
            inputClassName="h-9 bg-surface-3 hover:bg-surface-4 disabled:text-surface-6 disabled:bg-surface-2 hover:disabled:bg-surface-2 rounded border-none"
            @update:model-value="updateScheduleDetails"
            :class="scheduleValidation.expiryTime ? 'border border-error' : 'border-none'" />
        </div>
      </div>
      <p v-if="scheduleValidation.expiryTime" class="text-error text-tiny mt-1">{{ scheduleValidation.expiryTime }}</p>
    </div>

    <FormButton type="main" fullWidth :isLoading="saving" :disabled="!isDirty || saving" @click="saveSchedule">Save
      schedule
    </FormButton>
  </section>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
